export type ChargingPolicy = {
  id: string;
  label: string;
  description?: string;
  currency: string;
  sessionThresholds: SessionThresholds;
  tariffRules?: TariffRule[];
  // TODO: Review the below with OP models
  name?: string;
  status?: string;
  pricing?: string;
  associatedChargingGroups?: string[];
};

export type SessionThresholds = {
  minTimeMinutes?: number;
  maxTimeMinutes?: number;
  minKwhConsumed?: number;
  maxKwhConsumed?: number;
  maxCost?: number;
};

export type Tariff = {
  __initial?: boolean;
  components: TariffComponent[];
  descriptions?: TariffDescription[];
};

export type TariffRule = {
  __id?: string;
  __initial?: boolean;
  authorizationMethods: AuthorizationMethod[];
  customerGroups: string[];
  tariff: Tariff;
  validityPeriod: {
    enabled: boolean;
    start?: Date;
    end?: Date;
  };
};

export type TariffDescription = {
  __id: string;
  __initial?: boolean;
  languageCode: string;
  text: string;
};

export enum DaysOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export type TariffComponent = {
  __id?: string;
  __initial?: boolean;
  price?: Price;
  restrictions?: {
    enabled: boolean;
    timeOfDay?: {
      enabled: boolean;
      start?: number;
      end?: number;
    };
    daysOfWeek?: {
      enabled: boolean;
      values: DaysOfWeek[];
    };
    date?: {
      enabled: boolean;
      start?: Date;
      end?: Date;
    };
    duration?: {
      enabled: boolean;
      unit: TimeUnit;
      min?: string;
      max?: string;
    };
    energy?: {
      enabled: boolean;
      min?: string;
      max?: string;
    };
  };
};

export type CustomerGroup = {
  id?: string;
  label: string;
  description?: string;
  filters: {
    __id?: string;
    type: CustomerGroupFilterType;
    values: string[];
  }[];
};

export enum PriceType {
  TIME = 'TIME',
  ENERGY = 'ENERGY',
  FLAT = 'FLAT',
  PARKING_TIME = 'PARKING_TIME',
  DYNAMIC = 'DYNAMIC',
}

export const PriceTypeValues = Object.values(PriceType);

export type BasePrice = {
  type: PriceType;
  value: number;
  stepSize?: number;
};

export type TimePrice = {
  type: PriceType.TIME | PriceType.PARKING_TIME;
  unit: TimeUnit;
} & BasePrice;

export type DynamicPrice = {
  type: PriceType.DYNAMIC;
  surcharge: number;
  backup: number;
};

export type Price = BasePrice | TimePrice | DynamicPrice;

export enum TimeUnit {
  HOUR = 'HOUR',
  MINUTE = 'MINUTE',
}

export const TimeUnitValues = Object.values(TimeUnit);

export enum AuthorizationMethod {
  CHARGE_CARD = 'CHARGE_CARD',
  SCAN_TO_PAY = 'SCAN_TO_PAY',
  TAP_TO_PAY = 'TAP_TO_PAY',
}

export const AuthorizationMethodValues = Object.values(AuthorizationMethod);

export enum CustomerGroupFilterType {
  ACCOUNT = 'ACCOUNT',
  CONSUMER_CARD_BILLING_PLAN = 'CONSUMER_CARD_BILLING_PLAN',
  INFRA_TOKEN_PROVIDER = 'INFRA_TOKEN_PROVIDER',
  ISSUER_NAME = 'ISSUER_NAME',
  UID = 'UID',
  USER = 'USER',
}

export const CustomerGroupFilterTypeValues = Object.values(
  CustomerGroupFilterType
);

export type ChargingGroup = {
  id: string;
  label: string;
  description?: string;
  providerId: string;
  filters: ChargingGroupFilter[];
  updatedAt: Date;
  createdAt: Date;
};

export type ChargingGroupFilter = {
  connectors: ChargingGroupConnectorFilter[];
  accountIds: string[];
  locationIds: string[];
};

export type ChargingGroupConnectorFilter = {
  chargingStationId: string;
  evseId: number;
  connectorId: number;
};
